import { toJS } from 'mobx';
import { types as t, getParent, flow } from 'mobx-state-tree';

import { updateMSTObject } from 'helpers';

import { IntegrationData } from 'models/integrations';
import { LoadingState } from 'models/ui';
import { withEnv } from 'models/withEnv';

export const Integration = t
  .model('Integration', {
    triggerId: t.number,
    name: t.string,
    title: t.string,
    description: t.string,
    successMessage: t.string,
    data: t.array(IntegrationData),
    canRetrigger: false,
    canDelete: false,
    canPreview: false,
    isBlocked: false,
    triggered: false,
    ratio: t.maybeNull(t.number),
    multiple: false,

    // Internal, not from API
    type: t.string,
    state: LoadingState,
    refetching: false,
  })
  .views(withEnv)
  .views((self) => ({
    get parent() {
      return getParent(self, 2);
    },
    get valueDict() {
      return self.data.reduce((agg, entry) => {
        if (entry.type === 'multiple') {
          if (entry.value) {
            agg[entry.name] = toJS(entry.value);
          } else {
            agg[entry.name] = [];
          }
        } else if (entry.type === 'repeating') {
          let groups = [];
          entry.fields.forEach((group) => {
            let fields = {};
            group.forEach((field) => {
              fields[field.name] = field.value;
            });
            groups.push(fields);
          });
          agg[entry.name] = groups;
        } else {
          agg[entry.name] = entry.value;
        }
        return agg;
      }, {});
    },
  }))
  .actions((self) => ({
    setRatio(value) {
      self.ratio = value;
    },
    isValid() {
      let valid = true;
      self.data.forEach((data) => {
        if (!data.valid()) {
          valid = false;
        }
      });
      return valid;
    },
    hasErrors() {
      return !self.data.every((data) => data.error === '');
    },
    firstError() {
      let errorData = null;
      for (let i = 0; i < self.data.length; i++) {
        if (!self.data[i].valid()) {
          errorData = self.data[i];
          break;
        }
      }
      return errorData;
    },
    submitForJob: flow(function* () {
      const res = yield self.env.api.jobs.submitIntegration(
        self.parent.id,
        'job_published',
        self.triggerId,
        self.valueDict
      );
      if (!res.error) {
        self.triggered = true;
        self.showSuccess(self.successMessage);
      } else {
        self.env.showError(res.data || self.env.t('integration.submit.error'));
      }
    }),
    submitForApplication: flow(function* (retrigger, save, customData = {}) {
      let data = { ...self.valueDict, ...customData };
      if (retrigger) {
        data.retrigger = true;
      }
      if (save) {
        data.save = true;
      }
      const res = yield self.env.api.applications.submitIntegration(
        self.parent.id,
        'applicant_hired',
        self.triggerId,
        data
      );
      if (!res.error) {
        updateMSTObject(self, res.data);
        self.env.showSuccess(save ? self.env.t('integration.submit.save') : self.successMessage);
      } else {
        if (res.status >= 500) {
          self.env.showError(self.env.t('integration.submit.error.500'));
        } else {
          self.env.showError(res.data || self.env.t('integration.submit.error'));
        }
      }
    }),
    previewForApplication: flow(function* () {
      let data = self.valueDict;
      const res = yield self.env.api.applications.previewIntegration(
        self.parent.id,
        'applicant_hired',
        self.triggerId,
        data
      );
      if (!res.error) {
        return res.data;
      } else {
        self.env.showError(res.data || self.env.t('integration.preview.error'));
      }
    }),
    refetchForApplication: flow(function* (field) {
      self.refetching = true;
      let data = self.valueDict;
      data._edited_field = field;
      const res = yield self.env.api.applications.submitIntegration(
        self.parent.id,
        'applicant_hired',
        self.triggerId,
        data,
        true
      );
      if (!res.error) {
        self.data = res.data.data;
      } else {
        self.env.showError(res.data || self.env.t('integration.submit.error'));
      }
      self.refetching = false;
    }),
    deleteForApplication: flow(function* () {
      const res = yield self.env.api.applications.deleteIntegration(
        self.parent.id,
        'applicant_hired',
        self.triggerId
      );
      if (!res.error) {
        updateMSTObject(self, res.data);
      } else {
        self.env.showError(res.data || self.env.t('integration.delete.error'));
      }
    }),
    loadForJob: flow(function* () {
      if (self.state !== 'init') return;
      self.state = 'loading';
      const res = yield self.env.api.jobs.loadIntegration(
        self.parent.id,
        self.type,
        self.triggerId
      );
      if (!res.error) {
        self.state = 'loaded';
        updateMSTObject(self, res.data);
      } else {
        self.state = 'error';
      }
    }),
    loadForApplication: flow(function* () {
      if (self.state !== 'init') return;
      self.state = 'loading';
      const res = yield self.env.api.applications.loadIntegration(
        self.parent.id,
        self.type,
        self.triggerId
      );
      if (!res.error) {
        self.state = 'loaded';
        updateMSTObject(self, res.data);
      } else {
        self.state = 'error';
      }
    }),
  }));
